import styles from './styles.module.scss';

import React from 'react';
import dayjs from 'dayjs';
import {NavLink} from 'react-router-dom';
import {Row, Col} from 'reactstrap';

import {AuthConsumer, useAuth} from 'contexts/AuthContext';
import {useMembership} from 'contexts/MembershipContext';
import {SUBSCRIPTION_BILLING_METHOD} from 'configs/membership';
import {Link} from 'react-router-dom/cjs/react-router-dom.min';
import useQuery from 'hooks/useQuery';
import urlJoin from 'url-join';
import {CDN_IMAGE_URL} from 'configs';

const SOCIAL_LINKS = [
  {
    href: '//www.youtube.com/user/IAFandILAC',
    title: 'Go to Youtube :: IAF CertSearch',
    sr: 'Youtube',
    icon: urlJoin(CDN_IMAGE_URL, 'icons/youtube-light.svg'),
  },
  {
    href: '//twitter.com/IAF_Global',
    title: 'Go to Twitter :: IAF CertSearch',
    sr: 'Twitter',
    icon: urlJoin(CDN_IMAGE_URL, 'icons/x-light.svg'),
  },
  {
    href: '//www.linkedin.com/company/international-accreditation-forum-inc/',
    title: 'Go to LinkedIn :: IAF CertSearch',
    sr: 'LinkedIn',
    icon: urlJoin(CDN_IMAGE_URL, 'icons/linkedin-light.svg'),
  },
];

const Copyright = () => {
  const year = dayjs().format('YYYY');

  return (
    <div className="text-center text-lg-left">
      Copyright &copy; {year} IAF CertSearch. All Right Reserved
    </div>
  );
};

const Terms = () => (
  <nav className="text-center text-md-left mb-3 mb-md-0">
    <NavLink to="/policies/terms" className="mr-3">
      Terms and Conditions
    </NavLink>
    <NavLink to="/policies/privacy" className="mr-3">
      Privacy Policy
    </NavLink>
    <NavLink to="/contact">Contact</NavLink>
  </nav>
);

const Socials = () => (
  <nav className="text-center text-md-left mb-3 mb-md-0 d-flex flex-row">
    {SOCIAL_LINKS.map((item, index) => {
      const {sr, icon, ...rest} = item;
      return (
        <a
          key={index}
          target="_blank"
          className="mr-2"
          rel="noopener noreferrer"
          {...rest}
        >
          <span className="sr-only">{sr}</span>
          <div
            className="bg-secondary d-flex align-items-center justify-content-center"
            style={{height: '32px', width: '32px', borderRadius: '50%'}}
          >
            <img
              src={icon}
              alt={sr}
              height="auto"
              width="14px"
              className="m-3"
            />
          </div>
        </a>
      );
    })}
  </nav>
);

const SiteMap = () => {
  const {isAuth, isMainAccount} = useAuth();
  const {query} = useQuery();
  const {success} = query;
  const {isTrial, billingMethod, isSuspendedSubscription} = useMembership();

  const isManualMethod = billingMethod === SUBSCRIPTION_BILLING_METHOD.manual;

  const isPlansAllowedAccount =
    isAuth &&
    isMainAccount &&
    !success &&
    !isTrial &&
    !isManualMethod &&
    !isSuspendedSubscription;

  return (
    <nav>
      <Row>
        <Col sm="12" md="6" lg="4">
          <ul className="mb-3 mb-md-0">
            <li>
              <AuthConsumer>
                {({isAuth}) => {
                  if (isAuth) {
                    return (
                      <NavLink to="/dashboard" exact>
                        Dashboard
                      </NavLink>
                    );
                  } else {
                    return (
                      <NavLink to="/" exact>
                        Home
                      </NavLink>
                    );
                  }
                }}
              </AuthConsumer>
            </li>
            <li>
              <NavLink to="/about/iaf-certsearch" exact>
                IAF CertSearch
              </NavLink>
            </li>
            <li>
              <NavLink to="/about/iaf">
                International Accreditation Forum
              </NavLink>
            </li>
          </ul>
        </Col>
        <Col sm="12" md="6" lg="4">
          <ul className="mb-3 mb-md-0">
            <li>
              <NavLink to="/search/accreditation-bodies">
                Accreditation Bodies List
              </NavLink>
            </li>
            <li>
              <NavLink to="/search/certification-bodies">
                Certification Bodies List
              </NavLink>
            </li>
            {/*
            <li>
              <Link to="/about/qualitytrade">QualityTrade</Link>
            </li>
            */}
            <li>
              <NavLink to="/about/iaf-badge">IAF CertSearch Mark</NavLink>
            </li>
          </ul>
        </Col>

        <Col sm="12" md="6" lg="4">
          <ul>
            {(!isAuth || isPlansAllowedAccount) && (
              <li>
                <Link
                  to={isAuth ? '/membership/plans' : '/plans'}
                  target="_self"
                  rel="noopener noreferrer"
                >
                  Plans
                </Link>
              </li>
            )}
            <li>
              <a
                href="https://support.iafcertsearch.org/iaf-certsearch-support"
                target="_self"
                rel="noopener noreferrer"
              >
                IAF CertSearch Support
              </a>
            </li>
            <li>
              <a
                href="https://iaf.nu/en/iaf-certsearch-monthly-webinars"
                target="_self"
                rel="noopener noreferrer"
              >
                IAF CertSearch Monthly Webinars
              </a>
            </li>
            <li>
              <a
                href="https://support.iafcertsearch.org/iaf-certsearch-faq"
                target="_self"
                rel="noopener noreferrer"
              >
                FAQ
              </a>
            </li>
          </ul>
        </Col>
      </Row>
    </nav>
  );
};

const PageFooter = () => {
  return (
    <footer className={styles['page__footer']}>
      <div className="container">
        <SiteMap />
        <hr />
        <Row className="align-items-center pb-3">
          <div className="col-sm-12 col-lg-auto order-1 order-lg-2">
            <Row className="align-items-center">
              <div className="col-sm-12 col-md col-lg-auto">
                <Terms />
              </div>
              <div className="col-sm-12 col-md-auto">
                <Socials />
              </div>
            </Row>
          </div>

          <div className="col order-2 order-lg-1">
            <Copyright />
          </div>
        </Row>

        <div className="text-center">
          Powered by{' '}
          <strong>
            <a
              href="https://group.qualitytrade.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              QualityTrade
            </a>
          </strong>
        </div>
      </div>
    </footer>
  );
};

export default PageFooter;
